import {
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  SxProps,
} from "@mui/material";
import useIsDesktop from "components/hooks/useIsDesktop";
import useIsMobile from "components/hooks/useIsMobile";
import useLocalStorageState from "components/hooks/useLocalStorageState";
import { X } from "lucide-react";
import { useState } from "react";
import { OnboardingTask } from "./OnboardingTypes";
import { Task } from "./OnboardingTask";
import { OnboardingProgressBar } from "./OnboardingProgressBar";

interface OnboardingModuleProps {
  tasks: OnboardingTask[];
  title: string;
  subtitle: string;
  dismissKey: string;
  dismissDays?: number;
  collapseSx?: SxProps;
}

export const OnboardingModule = ({
  tasks,
  title,
  subtitle,
  dismissKey,
  dismissDays = 3,
  collapseSx,
}: OnboardingModuleProps) => {
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [closed, setClosed] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useLocalStorageState<string>(
    dismissKey,
    "",
  );

  // Don't show if dismissed and still within dismissal period
  if (dismissedUntil && new Date() < new Date(dismissedUntil)) {
    return null;
  }

  // Order tasks: completed first, then incomplete
  const orderedTasks = [
    ...tasks.filter((t) => t.completed),
    ...tasks.filter((t) => !t.completed),
  ];

  // Don't show if all tasks are completed
  if (orderedTasks.every((t) => t.completed)) {
    return null;
  }

  const onDismiss = () => {
    const DISMISS_DURATION = 1000 * 60 * 60 * 24 * dismissDays;
    setClosed(true);
    // Wait for the animation to finish before setting the dismissedUntil date
    setTimeout(() => {
      setDismissedUntil(
        new Date(new Date().getTime() + DISMISS_DURATION).toISOString(),
      );
    }, 1000);
  };

  return (
    <Collapse
      in={!closed}
      timeout={1000}
      sx={{ marginBottom: "36px", ...collapseSx }}
    >
      <Box
        sx={{
          display: "flex",
          padding: !isDesktop ? "20px 16px 20px 20px" : "24px",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          background: "#fff",
          width: "100%",
          height: "100%",
          minHeight: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "20px" : "24px",
            flexDirection: isDesktop ? "row" : "column",
            alignItems: "start",
            width: "100%",
            height: "100%",
          }}
        >
          {/* Left column */}
          <Box
            sx={{
              flex: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "start",
              height: "100%",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: isDesktop ? "12px" : "4px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Typography variant="h4">{title}</Typography>
                {!isDesktop && (
                  <IconButton sx={{ height: "unset" }} onClick={onDismiss}>
                    <X color="#8E9598" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="body">{subtitle}</Typography>
            </Box>
            {isDesktop && (
              <Button
                variant="text"
                color="primary"
                sx={{ height: "unset" }}
                onClick={onDismiss}
              >
                Dismiss
              </Button>
            )}
          </Box>

          {/* Center column: checklist */}
          <Box sx={{ flex: 5, minWidth: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {orderedTasks.map((task) => (
                <Task key={task.label} task={task} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right column: progress */}
        {isDesktop && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
            }}
          >
            <OnboardingProgressBar
              completedCount={orderedTasks.filter((t) => t.completed).length}
              totalCount={orderedTasks.length}
            />
          </Box>
        )}
      </Box>
    </Collapse>
  );
};
